import { makeStyles, Theme, createStyles } from "@material-ui/core";
import * as React from "react";
import InstallationTable from "../../../components/InstallationTable";
import { useHubEventDateWindow } from "../../../core/hooks/useHubHooks";
import InstallationModel from "../../../core/models/Installation";
import {
  EVENT_EXPORT_EXCEL_TRIPS_HISTORY,
  EVENT_EXPORT_PDF_TRIPS_HISTORY,
  EVENT_INSTALLATION_TRIPS_HISTORY_FILTER_DATE_CHANGE,
  EVENT_REFRESH_INSTALLATION_DETAIL_PAGE,
} from "../../../Events";
import {
  FETCH_EXCEL_FILE_TRIPS_HISTORY,
  FETCH_INSTALLATION_TRIPS_READINGS,
  FETCH_PDF_FILE_FROM_HTML,
} from "../../../Fedx";
import { getDateFromTimezonePtBrToGMT } from "../../../helpers/datetime";
import { formatValue } from "../../../helpers/utility";
import { useHubx, useHubXSubscribe } from "../../../HubX";
import { InstallationLevelServiceFetchParams } from "../../../services/InstallationLevelService";
import orderBy from "lodash/orderBy";
import { ITripsV2 } from "../../../core/contracts/IInstallationLevel";
import { IExportExcel } from "../../../core/contracts/IExportExcel";
import IHtml from "../../../core/contracts/IHtml";
import ReportGrid from "../../../components/Report/ReportGrid";
import { renderToString } from "react-dom/server";
import ReportSimpleTable from "../../../components/Report/ReportSimpleTable";
import useAuth from "../../../core/hooks/useAuth";
import { ContactlessOutlined } from "@material-ui/icons";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      // WARNING
      // 100vh - header - tabs - toolbar
      maxHeight: `calc(100vh - ${theme.spacing(3)}px - 67px - 77px - 16px - ${
        theme.mixins.toolbar.minHeight
      }px)`,
    },
  })
);

type InstallationTripsHistoryProps = {
  installation?: InstallationModel;
};

type ReportDataType = {
  date: string;
  volume: string;
  tripId: string;
  accumulation: number;
};

function InstallationTripsHistory({ installation }: InstallationTripsHistoryProps) {
  const [loading, setLoading] = React.useState(false);
  const [useTankData, setUseTankData] = React.useState(false);
  const [exportExcel, setExportExcel] = React.useState(false);
  const [exportPdf, setExportPdf] = React.useState(false);
  const [InstallationTripsData, setInstallationTripsData] = React.useState<ITripsV2[] | null>(null);
  // eslint-disable-next-line @typescript-eslint/ban-types
  const [reportData, setReportData] = React.useState<ReportDataType[]>([]);

  const [arrayTable, setArrayTable] = React.useState<any>([]);

  const filterInterval = useHubEventDateWindow(
    EVENT_INSTALLATION_TRIPS_HISTORY_FILTER_DATE_CHANGE,
    true
  );

  const classes = useStyles();
  const hub = useHubx();
  const authProvider = useAuth();
  useHubXSubscribe(EVENT_EXPORT_EXCEL_TRIPS_HISTORY, setExportExcel);
  useHubXSubscribe(EVENT_EXPORT_PDF_TRIPS_HISTORY, setExportPdf);
  useHubXSubscribe(EVENT_REFRESH_INSTALLATION_DETAIL_PAGE, fetchData);

  async function fetchData() {
    if (installation?.installation_id) {
      setLoading(true);

      const fetchParams: InstallationLevelServiceFetchParams = {
        installation_id: installation?.installation_id,
        startDate: filterInterval?.start || undefined,
        endDate: filterInterval?.end || undefined,
      };

      if (fetchParams.startDate && fetchParams.endDate) {
        const APIInstallationResponse: ITripsV2[] = await hub.request(
          FETCH_INSTALLATION_TRIPS_READINGS,
          fetchParams
        );

        setInstallationTripsData(APIInstallationResponse);
      }
      setLoading(false);
    }
  }

  async function fetchFile(exportFile: IExportExcel) {
    await hub.request(FETCH_EXCEL_FILE_TRIPS_HISTORY, exportFile);
  }

  async function fetchPdf(htmlFile: IHtml) {
    await hub.request(FETCH_PDF_FILE_FROM_HTML, htmlFile);
  }

  async function mapReportData(date: string, volume: string, tripId: string, accumulation: number) {
    const newReportData: ReportDataType = {
      date: date,
      volume: volume,
      tripId: tripId,
      accumulation: accumulation,
    };

    setReportData((reportData) => [...reportData, newReportData]);
  }

  const memoDataToDisplay = React.useMemo(() => {
    let data;
    setReportData([]);

    if (InstallationTripsData) {
      data = orderBy(
        InstallationTripsData,
        function (o) {
          return o?.trip_end_date ? new Date(o?.trip_end_date).getTime() : 0;
        },
        "desc"
      );
    }

    let acumulado = 0;
    const datareverse = data || [];

    for (let i = datareverse.length - 1; i >= 0; i--) {
      acumulado += Number(datareverse[i].trip_volume);
      datareverse[i].trip_accumulation = acumulado;
    }

    return (
      data?.map((d, idx) => {
        mapReportData(
          getDateFromTimezonePtBrToGMT(d.trip_end_date),
          d.trip_volume,
          d.trip_number,
          d.trip_accumulation
        );

        return authProvider.hasHighLevel()
          ? [
              <strong key={`date_${idx}`}>{getDateFromTimezonePtBrToGMT(d.trip_end_date)}</strong>,
              `${d?.trip_number}`,
              `${formatValue(d?.trip_volume ? Number(d.trip_volume) : 0)}`,
              `${formatValue(d?.trip_accumulation ? Number(d.trip_accumulation) : 0)}`,
            ]
          : [];
      }) || []
    );
  }, [useTankData, InstallationTripsData, installation]);

  //console.log("isso é um teste 2 E EU DEVO SER APAGADO", memoDataToDisplay);

  React.useEffect(() => {
    const doFetch = async () => await fetchData();
    doFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installation?.installation_id, installation?.tanks, filterInterval]);

  const headers = React.useMemo(() => {
    const headersAux = [
      "Data",
      "ID da Viagem",
      "Volume Descarregado (m³ ou kg)",
      "Acumulado (m³ ou kg)",
    ];
    return headersAux;
  }, [memoDataToDisplay]);

  const headersFileExport = React.useMemo(() => {
    const headersFileExport = [
      "Data",
      "ID da Viagem",
      "Volume Descarregado (m³ ou kg)",
      "Acumulado (m³ ou kg)",
    ];

    return headersFileExport;
  }, [memoDataToDisplay]);

  if (exportExcel) {
    // Executa aqui
    if (reportData) {
      console.log(reportData);
      const exportFile: IExportExcel = {
        headers: headersFileExport,
        data: reportData,
        fileName: installation?.product_code
          ? "Historico_de_viagem_" + installation.product_code + " #" + installation.installation_id
          : "Historico_de_viagem",
      };

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      fetchFile(exportFile);
    }

    setExportExcel(false);
  }

  if (exportPdf) {
    const exportFile: IHtml = {
      htmlString: renderToString(
        <ReportGrid headerReport="Histórico de Viagem">
          <ReportSimpleTable rows={memoDataToDisplay} header={headers} />
        </ReportGrid>
      ),
      fileName: installation?.product_code
        ? "Histórico_de_viagem_" + installation.product_code + " #" + installation.installation_id
        : "Histórico_de_viagem",
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchPdf(exportFile);

    setExportPdf(false);
  }

  return (
    <div className={classes.root}>
      <InstallationTable desableClick header={headers} rows={memoDataToDisplay} loading={loading} />
    </div>
  );
}

export default InstallationTripsHistory;
